import { Component, Input, Output, EventEmitter, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslationService } from '../../../services/translation.service';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import {
  formatAppointmentDate,
  formatAppointmentTime,
  formatScheduledTime,
} from '../../utils/date-time.utils';
import { AppointmentsService } from '../../../components/schedule/services/appointments.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../services/toast.service';

export interface RescheduleData {
  date: string;
  time: string;
  reason: string;
}

export interface AppointmentData {
  id?: string;
  patientName?: string;
  patient?: { name: string } | string;
  title?: string;
  time?: string;
  date?: string;
  type?: string;
}

@Component({
  selector: 'app-reschedule-appointment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerComponent, TimePickerComponent],
  templateUrl: './reschedule-appointment-modal.component.html',
  styleUrl: './reschedule-appointment-modal.component.scss',
})
export class RescheduleAppointmentModalComponent {
  @Input() isOpen: boolean = false;
  @Input() appointment: AppointmentData | null = null;
  @Output() modalClose = new EventEmitter<void>();
  @Output() reschedule = new EventEmitter<RescheduleData>();

  rescheduleData = signal<RescheduleData>({
    date: '',
    time: '',
    reason: '',
  });

  // Computed signal for minimum date (tomorrow)
  minDate = computed(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  });

  // Computed signal to format appointment date based on language
  formattedAppointmentDate = computed(() => {
    const currentLang = this.translationService.getCurrentLanguage();
    return formatAppointmentDate(this.appointment?.date, currentLang);
  });

  // Computed signal to format appointment time based on language
  formattedAppointmentTime = computed(() => {
    const currentLang = this.translationService.getCurrentLanguage();
    return formatAppointmentTime(this.appointment?.time, currentLang);
  });

  constructor(
    public readonly translationService: TranslationService,
    private readonly appointmentsService: AppointmentsService,
    private readonly authService: AuthService,
    private readonly toastService: ToastService
  ) {}

  closeModal() {
    this.resetForm();
    this.modalClose.emit();
  }

  saveReschedule() {
    if (!this.isRescheduleFormValid()) {
      return;
    }

    const data = this.rescheduleData();
    const currentUser = this.authService.currentUser();
    const appointmentId = this.appointment?.id;

    if (!currentUser || !appointmentId) {
      this.toastService.showError('Missing required information');
      return;
    }

    // Format the new scheduled time using formatScheduledTime
    const new_scheduled_time = formatScheduledTime(data.date, data.time);

    const payload = {
      new_scheduled_time,
    };

    this.appointmentsService
      .rescheduleAppointment(currentUser.hospital_id, appointmentId, payload)
      .subscribe({
        next: () => {
          const successMsg = this.translationService.translate('reschedule.success');
          this.toastService.showSuccess(successMsg);
          this.reschedule.emit(this.rescheduleData());
          this.resetForm();
          this.closeModal();
        },
        error: () => {
          const errorMsg = this.translationService.translate('reschedule.error');
          this.toastService.showError(errorMsg);
        },
      });
  }

  updateRescheduleField<K extends keyof RescheduleData>(field: K, value: RescheduleData[K]) {
    this.rescheduleData.update((data) => ({
      ...data,
      [field]: value,
    }));
  }

  isRescheduleFormValid(): boolean {
    const data = this.rescheduleData();
    return !!(data.date && data.time);
  }

  resetForm() {
    this.rescheduleData.set({
      date: '',
      time: '',
      reason: '',
    });
  }

  getPatientName(): string {
    if (this.appointment?.patientName) {
      return this.appointment.patientName;
    }
    if (this.appointment?.patient) {
      if (typeof this.appointment.patient === 'string') {
        return this.appointment.patient;
      }
      return this.appointment.patient.name;
    }
    if (this.appointment?.title) {
      return this.appointment.title;
    }
    return 'John Smith';
  }

  getDefaultReason(): string {
    const patientName = this.getPatientName();
    const type = this.appointment?.type || 'General Health Consultation';
    return `${patientName} ${type}`;
  }

  onSelectChange(event: Event, field: keyof RescheduleData): void {
    const target = event.target as HTMLSelectElement;
    if (target) {
      this.updateRescheduleField(field, target.value);
    }
  }

  onInputChange(event: Event, field: keyof RescheduleData): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (target) {
      this.updateRescheduleField(field, target.value);
    }
  }

  onDateChange(date: Date | null): void {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      this.updateRescheduleField('date', `${year}-${month}-${day}`);
    } else {
      this.updateRescheduleField('date', '');
    }
  }

  onTimeChange(time: string): void {
    this.updateRescheduleField('time', time);
  }
}
